import {
  AfterViewChecked,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AgmInfoWindow, AgmMap, LatLngLiteral, MapTypeStyle} from '@agm/core';
import {FormatEvent} from 'agm-spiderfier/services/google-spider-types';
import {SiteDto} from '../../objects/site.dto';
import {PlannedSiteFilterModel} from '../../objects/planned-site-filter.model';
import {InstallationWeekFilterEnum} from '../../objects/installation-week-filter.enum';
import {SiteInstallationStateEnum} from '../../objects/site-installation-state.enum';
import {CustomerDtoModel} from '../../../../models/customer-dto.model';
import {ServiceTechnicianDtoModel} from '../../../../models/service-technician-dto.model';
import {User} from '../../../../models/user-model';
import {JuconnUserRoleEnum} from '../../../../models/juconn-user-role.enum';
import {DataService} from '../../../../services/data.service';
import {UserService} from '../../../../services/user.service';
import {StatusFilterEnum} from "../../objects/status-filter.enum";

@Component({
  selector: 'app-planner-map',
  templateUrl: './planner-map.component.html',
  styleUrls: ['./planner-map.component.scss']
})
export class PlannerMapComponent implements OnInit, OnDestroy, AfterViewChecked, OnChanges {
  @Input() public sites: SiteDto[] = [];
  @Input() public filter: PlannedSiteFilterModel = {
    jucCustomerId: undefined,
    installationPersonEmail: 'null',
    installationWeekFilter: InstallationWeekFilterEnum.ALL
  };
  @Input() public customers: CustomerDtoModel[] = [];
  @Input() public serviceTechnicians: ServiceTechnicianDtoModel[] = [];
  @Input() public installationWeekFilterOptions: { value: InstallationWeekFilterEnum; label: string }[] = [];
  @Input() public statusOptions: { value: StatusFilterEnum; label: string }[] = [];
  @Output() public refreshRequested: EventEmitter<PlannedSiteFilterModel> = new EventEmitter<PlannedSiteFilterModel>();

  public get fitBounds(): boolean {
    return !this.pinEditMode && this.first;
  }

  @ViewChild(AgmMap)
  public map: AgmMap;

  user: User;
  public modalSiteDetail = {showModal: false};
  public modalCreateSiteDetail = {showModal: false};

  public latitude: number;
  public longitude: number;
  public zoom = 7;
  public tempLatitude: number;
  public tempLongitude: number;
  public tempZoom: number;

  public showMap = false;
  public showLegend = false;

  public first = true;
  public selectedSite: SiteDto;

  private markerMap: any = {};

  public createSite: SiteDto;

  public assignmentMode: boolean;
  public sitesMarkedForAssignment: SiteDto[] = [];
  public saveAssignmentMode: boolean;
  public assignmentInstallationPersonEmail: string;
  public assignmentInstallationWeekFilter: number;

  public mapStyles: MapTypeStyle[] = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{visibility: 'off'}]
    },
    {
      featureType: 'landscape',
      elementType: 'labels',
      stylers: [{visibility: 'off'}]
    },
  ];

  public currentPosition: LatLngLiteral;
  public currentPositionIcon = {
    url: './assets/images/icons/map/current_location.png',
    scaledSize: {
      width: 30,
      height: 30
    },
    anchor: {
      x: 15,
      y: 15
    }
  };

  public editSite: SiteDto;
  public pinEditMode: boolean;
  public pinLatLng: { lat: number, lng: number };
  public oldLatLng: { lat: number, lng: number };

  private nonZoomElements: HTMLElement[] = [];

  constructor(
    private shareService: UserService,
    private dataService: DataService,
  ) {
    navigator.geolocation.getCurrentPosition((position) => {
      this.currentPosition = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      if (!this.sites.length) {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.tempLatitude = position.coords.latitude;
        this.tempLongitude = position.coords.longitude;
        this.zoom = 7;
        this.tempZoom = 7;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter']?.currentValue) {
      this.filter = {...this.filter, ...changes['filter'].currentValue};
    }

    if (changes['sites'] && !this.sites.length) {
      this.latitude = this.currentPosition?.lat || 51.1657;
      this.longitude = this.currentPosition?.lng || 10.4515;
      this.zoom = 7;
      this.tempLatitude = this.latitude;
      this.tempLongitude = this.longitude;
      this.tempZoom = this.zoom;
    }
  }

  ngAfterViewChecked(): void {
    const targetElements: NodeList = document.querySelectorAll('.non-zoom-element') as NodeList;

    targetElements.forEach((targetElement: HTMLElement) => {
      if (!this.nonZoomElements.includes(targetElement)) {
        this.nonZoomElements.push(targetElement);

        targetElement.addEventListener('wheel', (e: WheelEvent) => {
          if (e.ctrlKey) {
            e.preventDefault();
          }
        }, {passive: false});

        targetElement.addEventListener('touchstart', (e: TouchEvent) => {
          if (e.touches.length > 1) {
            e.preventDefault();
          }
        }, {passive: false});
      }
    });
  }

  ngOnDestroy(): void {
  }

  async ngOnInit(): Promise<void> {
    document.addEventListener('gesturestart', function (e) {
      e.preventDefault();
    });

    this.assignmentMode = false;
    this.first = true;
    await this.init();
  }

  private async init(): Promise<void> {
    this.user = this.shareService.user;

    this.showMap = true;
  }

  public updateJucCustomerIdFilter(jucCustomerId: number): void {
    this.filter.jucCustomerId = jucCustomerId;
    this.requestRefresh();
  }

  public updateInstallationPersonEmailFilter(installationPersonEmail: string): void {
    this.filter.installationPersonEmail = installationPersonEmail;
    this.requestRefresh();
  }

  public updateInstallationWeekFilter(installationWeekFilter: InstallationWeekFilterEnum): void {
    this.filter.installationWeekFilter = installationWeekFilter;
    this.requestRefresh();
  }

  public updateSiteStatusesFilter(siteStatuses: StatusFilterEnum[]): void {
    this.filter.siteStatuses = siteStatuses;
    this.requestRefresh();
  }

  public updateAssignmentInstallationPersonEmailFilter(assignmentInstallationPersonEmail: string): void {
    this.assignmentInstallationPersonEmail = assignmentInstallationPersonEmail;
  }

  public updateAssignmentInstallationWeekFilter(assignmentInstallationWeekFilter: number): void {
    this.assignmentInstallationWeekFilter = assignmentInstallationWeekFilter;
  }

  public updateSearchTextFilter(searchText: string): void {
    this.filter.searchTerm = searchText;
    this.requestRefresh();
  }

  public isPlannerAndIsACustomerSelected(): boolean {
    return this.isPlanner() && this.filter.jucCustomerId !== -1 && this.filter.jucCustomerId !== undefined;
  }

  public enableAssignmentSaveMode(): void {
    this.saveAssignmentMode = true;
  }

  public openCreateSiteModal(): void {
    this.createSite = {
      city: null,
      street: null,
      streetNumber: null,
      additionalName: null,
      postCode: null,
      contactPersonName: null,
      contactPersonNumber: null,
      customerId: this.filter.jucCustomerId,
      customerName: this.getFilteredCustomerName(),
      installationWeek: null,
      installationPersonEmail: null,
      siteInstallationState: SiteInstallationStateEnum.PLANNED,
      latitude: null,
      longitude: null,
      salesforceId: null,
      serialNumbers: [],
      installedControl: null,
      note: null
    };
    this.modalCreateSiteDetail.showModal = true;
  }

  public isPlanner(): boolean {
    return this.user && this.user.roles && this.user.roles.includes(JuconnUserRoleEnum.user_planner);
  }

  public getFilteredCustomerName(): string {
    return this.customers.find(c => c.id === this.filter.jucCustomerId)?.customerName;
  }

  public enableAssignmentMode(): void {
    this.filter.installationWeekFilter = InstallationWeekFilterEnum.OPENED;
    this.assignmentInstallationPersonEmail = null;
    this.assignmentInstallationWeekFilter = null;
    this.sitesMarkedForAssignment = [];
    this.assignmentMode = true;
    this.requestRefresh();
  }

  public zoomChange(zoom: number): void {
    this.tempZoom = zoom;
  }

  public centerChange(latLng: LatLngLiteral): void {
    this.tempLatitude = latLng.lat;
    this.tempLongitude = latLng.lng;
  }

  public idle(): void {
    this.latitude = this.tempLatitude;
    this.longitude = this.tempLongitude;
    this.zoom = this.tempZoom;
  }

  public formatSpider(event: FormatEvent): void {
    const id = event.marker[Object.keys(event.marker).find(k => k.startsWith('closure_uid_'))];
    if (!this.markerMap[id]) {
      this.markerMap[id] = (event.marker as any).icon;
    }

    const split = this.markerMap[id].split('/');
    const siteId = Number.parseInt(event.marker['label'], 10);
    let inverseText = '';
    if (this.assignmentMode || this.saveAssignmentMode) {
      if (!this.sitesMarkedForAssignment.find(s => s.id === siteId)) {
        inverseText = 'inverse_';
      }
    }

    const iconURL = './assets/images/icons/map/' +
      (event.status === 'SPIDERFIABLE' ? 'multiple_' : '') + inverseText + split[split.length - 1];

    event.marker.setIcon(iconURL);
  }

  public trackByFn(index: number, item: SiteDto): number {
    return item.id;
  }

  public getMarkerIcon(site: SiteDto): string {
    let suffix: string;
    if (!!site.note) {
      suffix = 'notes_site_pin.png';
    } else if (site.siteInstallationState === SiteInstallationStateEnum.PLANNED && site.installationWeek && site.installationPersonEmail) {
      suffix = 'planned_site_pin.png';
    } else if (site.siteInstallationState === SiteInstallationStateEnum.PLANNED) {
      suffix = 'unplanned_site_pin.png';
    } else if (site.siteInstallationState === SiteInstallationStateEnum.ONGOING) {
      suffix = 'ongoing_site_pin.png';
    } else {
      suffix = 'closed_site_pin.png';
    }
    if ((this.assignmentMode || this.saveAssignmentMode) && !this.isSiteMarkedForAssignment(site)) {
      suffix = 'inverse_' + suffix;
    }
    return './assets/images/icons/map/' + suffix;
  }

  public onMouseOver(infoWindow: AgmInfoWindow, event: any): void {
    try {
      if (event.lastOpen && event.lastOpen.isOpen) {
        event.lastOpen.close();
      }
    } catch (error) {
      // nothing here
    }
    infoWindow.open();
  }

  public onMouseOut(infoWindow: AgmInfoWindow): void {
    infoWindow.close();
  }

  public showDetails(site: SiteDto): void {
    if (this.pinEditMode) {
      return;
    }
    if (!this.assignmentMode) {
      this.selectedSite = site;
      this.modalSiteDetail.showModal = true;
    } else if (this.assignmentMode && !this.saveAssignmentMode) {
      if (this.sitesMarkedForAssignment.includes(site)) {
        this.sitesMarkedForAssignment = this.sitesMarkedForAssignment.filter(s => s !== site);
      } else {
        this.sitesMarkedForAssignment.push(site);
      }
    }
  }

  public isSiteMarkedForAssignment(site: SiteDto): boolean {
    return this.sitesMarkedForAssignment.includes(site);
  }

  public isAnySiteMarkedForAssignment(): boolean {
    return this.filter.jucCustomerId !== -1 && this.isPlanner() && this.sitesMarkedForAssignment.length > 0;
  }

  public canSitesMarkedForAssignmentBeSaved(): boolean {
    const isEmailFilledIn = this.assignmentInstallationPersonEmail && this.assignmentInstallationPersonEmail != null;
    const isWeekFilledIn = this.assignmentInstallationWeekFilter && this.assignmentInstallationWeekFilter != null;
    const isWeekValid = this.assignmentInstallationWeekFilter > 0 && this.assignmentInstallationWeekFilter <= 53 && this.assignmentInstallationWeekFilter % 1 === 0;
    const anySelected = this.sitesMarkedForAssignment.length > 0;

    return (isEmailFilledIn && anySelected) || (isWeekValid && isWeekFilledIn && anySelected);
  }

  public async updateMarkedSites(): Promise<void> {
    if (this.canSitesMarkedForAssignmentBeSaved()) {
      const sitesToUpdate: SiteDto[] = [];
      this.sitesMarkedForAssignment.forEach(s => {
        const site = {...s};
        if (this.assignmentInstallationPersonEmail && this.assignmentInstallationPersonEmail != null) {
          site.installationPersonEmail = this.assignmentInstallationPersonEmail;
        }
        if (this.assignmentInstallationWeekFilter && this.assignmentInstallationWeekFilter != null) {
          site.installationWeek = this.assignmentInstallationWeekFilter;
        }
        sitesToUpdate.push(site);
      });

      await this.dataService.updatePlannedSites(sitesToUpdate);
      this.cancelAssigment();
      this.requestRefresh();
    }
  }

  public cancelAssigment(): void {
    this.sitesMarkedForAssignment = [];
    this.assignmentMode = false;
    this.saveAssignmentMode = false;
  }

  public moveSite(site: SiteDto): void {
    this.editSite = site;
    this.pinEditMode = true;
    this.pinLatLng = {lat: site.latitude, lng: site.longitude};
    this.oldLatLng = {lat: site.latitude, lng: site.longitude};
  }

  markerDrag($event: any): void {
    this.pinLatLng = {lat: $event.coords.lat, lng: $event.coords.lng};
    this.editSite.latitude = $event.coords.lat;
    this.editSite.longitude = $event.coords.lng;
  }

  async saveNewPosition(): Promise<void> {
    if (this.oldLatLng.lat !== this.pinLatLng.lat || this.oldLatLng.lng !== this.pinLatLng.lng) {
      this.editSite.latitude = this.pinLatLng.lat;
      this.editSite.longitude = this.pinLatLng.lng;
      this.editSite.coordinatesAddedManually = true;
      await this.dataService.updatePlannedSite(this.editSite);

      this.endEditMode();
      this.requestRefresh();
    }
  }

  cancelChangePosition(): void {
    this.editSite.latitude = this.oldLatLng.lat;
    this.editSite.longitude = this.oldLatLng.lng;
    this.endEditMode();
  }

  getCurrentWeekNumber(): number {
    const now = new Date();
    const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 1).getTime()) / 86400000) + 1;

    const weekNumber = Math.ceil((dayOfYear + (new Date(now.getFullYear(), 0, 1).getDay() - 1)) / 7);

    return weekNumber === 0 ? 1 : weekNumber;
  }

  public onSiteUpdated(): void {
    this.requestRefresh();
  }

  private endEditMode(): void {
    this.pinEditMode = false;
    this.editSite = null;
  }

  private requestRefresh(): void {
    this.refreshRequested.emit({...this.filter});
  }
}
